import { Grid } from "@mui/material";
import styles from "../../Styles/order.module.css";
import {
  InstructorModel,
  OrderValidationSchema,
  ProductsModel,
} from "../../Utils/Models";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getData } from "../../Services/getData";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import StatusPanel from "./StatusPanel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponse } from "axios";
import useCustomMemo from "../../Hooks/useCustomMemo";
import GeneralInfo from "./Tabs/GeneralInformation";
import {
  fetchAllDocumentTypes,
  fetchInstructors,
  fetchProducts,
  isMobile,
} from "../../Utils/Utils";
import { getFromCacheOrFetch } from "../../Utils/getFromCacheOrFetch";
import withRole from "../../Hooks/withRole";

function Order(props: any) {
  const [products, setProducts] = useState([] as ProductsModel[]);
  const [instructors, setInstructors] = useState([] as InstructorModel[]);
  const [allDocumentTypes, setAllDocumentTypes] = useState<any[]>([]);
  const [clientDocuments, setClientDocuments] = useState<any[]>([]);
  const { id } = useParams();
  const {
    register,
    control,
    getValues,
    formState,
    reset,
    setValue,
    watch,
    clearErrors,
    trigger,
  } = useForm({
    resolver: yupResolver(OrderValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });
  const navigate = useNavigate();
  const memo = useCustomMemo();
  const managements = memo?.managements || [];
  const [triggerReload, setTriggerReload] = useState<undefined | boolean>();
  const workPointId = props?.restrictByWorkPoint
    ? localStorage.getItem("userWorkPointId")
    : undefined;

  /**
   *
   */
  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<ProductsModel[]>({
      cacheKey: "products",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchProducts(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setProducts(data);
      }
    });

    getFromCacheOrFetch<InstructorModel[]>({
      cacheKey: `instructors_${workPointId ?? "all"}`,
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchInstructors(workPointId),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setInstructors(data);
      }
    });

    getFromCacheOrFetch<any[]>({
      cacheKey: "allDocumentTypes",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchAllDocumentTypes(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setAllDocumentTypes(data);
      }
    });

    if (id) getOrder(id);

    return () => {
      mounted = false;
    };
  }, []);

  /**
   *
   * @param id
   */
  const getOrder = (id: string | undefined) => {
    getData(`${endpoints.ordersEndpoint}/${id}`).then((res: AxiosResponse) => {
      if (!res || !res?.data) return;
      reset(res.data, { keepDefaultValues: false });

      // Fetch client documents if client exists
      const clientId = res.data?.client_id?._id;
      const orderId = res.data?._id;
      if (clientId && orderId) {
        getData(
          endpoints.client.getClientDocuments
            .replace(":clientId", clientId)
            .replace(":orderId", orderId)
        ).then((docsRes: AxiosResponse) => {
          if (docsRes?.data) {
            setClientDocuments(docsRes.data);
          }
        });
      }
    });
  };

  /**
   *
   * @param st
   */
  function handleChange(st?: any, passedField?: string) {
    setValue("status", st._id);
    if (passedField) {
      setValue(passedField as any, getValues(passedField as any));
    }
    triggerSaveOrder();
  }

  const triggerSaveOrder = () => {
    saveOrder(getValues());
    getOrder(getValues("_id"));
  };
  /**
   *
   * @param data
   */
  const saveOrder = (data: any) => {
    const orderId = getValues("_id");
    if (orderId) {
      updateOrder(data);
    } else {
      postData(endpoints.ordersEndpoint, data).then((res: AxiosResponse) => {
        if (res?.data) {
          navigate(`${localUrlEnum.order}/${res?.data?._id}`);
          setValue("_id", res?.data?._id);
          setValue("status", res?.data?.status);
        }
      });
    }
  };

  /**
   *
   * @param data
   */
  const updateOrder = (data: any) => {
    const orderId = getValues("_id");
    const url = `${endpoints.ordersEndpoint}/${orderId}`;
    // if (Object.keys(formState.dirtyFields).length !== 0)
    updateData(url, data).then((res: AxiosResponse) => {
      if (res?.data) {
        // reset(res.data, {
        //   keepValues: false,
        //   keepDirty: false,
        //   keepDefaultValues: false,
        // });
      }
    });
  };

  return (
    <div className={styles.mainContainer}>
      <Grid
        container
        spacing={1}
        style={{ width: isMobile() ? "100vw" : "auto" }}
      >
        {/**STATUS */}
        <Grid item xs={12} sm={12} md={12}>
          <StatusPanel
            name="StatusPanel"
            selectedOption={watch("status")}
            handleChange={handleChange}
            allOptions={memo.storage?.orderStatuses}
            getValues={getValues}
            control={control}
            reset={reset}
            setTriggerReload={setTriggerReload}
            triggerReload={triggerReload}
            clientDocuments={clientDocuments}
          ></StatusPanel>
        </Grid>
        <GeneralInfo
          instructors={instructors}
          managements={managements}
          products={products}
          allDocumentTypes={allDocumentTypes}
          saveOrder={triggerSaveOrder}
          getOrder={() => getOrder(id)}
          triggerReload={triggerReload}
          setTriggerReload={setTriggerReload}
          clientDocuments={clientDocuments}
          setClientDocuments={setClientDocuments}
          formData={{
            register,
            control,
            getValues,
            formState,
            reset,
            setValue,
            watch,
            clearErrors,
            trigger,
          }}
          name="GeneralInfo"
        />
      </Grid>
    </div>
  );
}

export default withRole(Order);
